import { useEffect, useMemo, useState } from "react";
import { Table } from "react-bootstrap";

import type { TableColumnDef } from "./types";
import { TIER_STYLES, DEFAULT_TIER_STYLE } from "./types";
import { getChartEntry, canConvertToChart } from "./registry";
import { getGenericTableData } from "./data/transformers";
import { registerTableMenu, unregisterTableMenu } from "./tableMenuRegistry";

type ChartTableViewProps = {
  chartId: string;
  title: string;
  // Widget description from the dashboard detail API — prepended to CSV downloads.
  description?: string | null;
  sourceChartType: string;
  data: unknown;
  fillHeight: boolean;
  onSetChartType: (type: string) => void;
};

// ---------------------------------------------------------------------------
// Minimal CSV/XLS download helpers.
// These replace the old exporters.ts module and live here because downloads
// are a table-view concern — Highcharts' built-in downloadCSV only works on
// chart series data, not our custom table rows.
// ---------------------------------------------------------------------------

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function exportTableCsv(
  title: string,
  columns: TableColumnDef[],
  rows: Array<Record<string, unknown>>,
  description?: string | null,
) {
  const escape = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const meta = description
    ? `${escape("Widget")},${escape(title)}\n${escape("Description")},${escape(description)}\n\n`
    : "";
  const header = columns.map((c) => escape(c.header)).join(",");
  const body = rows
    .map((row) => columns.map((c) => escape(row[c.key])).join(","))
    .join("\n");
  downloadBlob(
    new Blob([`${meta}${header}\n${body}`], { type: "text/csv;charset=utf-8;" }),
    `${(title || "chart").replace(/\s+/g, "_")}_table.csv`,
  );
}

function exportTableXls(
  title: string,
  columns: TableColumnDef[],
  rows: Array<Record<string, unknown>>,
) {
  const header = columns.map((c) => c.header).join("\t");
  const body = rows
    .map((row) => columns.map((c) => String(row[c.key] ?? "")).join("\t"))
    .join("\n");
  downloadBlob(
    new Blob([`${header}\n${body}`], {
      type: "application/vnd.ms-excel;charset=utf-8;",
    }),
    `${(title || "chart").replace(/\s+/g, "_")}_table.xls`,
  );
}

// ---------------------------------------------------------------------------
// Cell display formatting — applied only in the rendered <td>, not in exports.
// ---------------------------------------------------------------------------
function formatCellValue(value: unknown, col: TableColumnDef): string | number {
  if (col.key === "percentage" && typeof value === "number")
    return `${value.toFixed(2)}%`;
  if (
    (col.key === "pts" ||
      col.key === "pa_cases" ||
      col.key === "pa_approved_cases") &&
    typeof value === "number"
  )
    return value.toLocaleString();
  return value as string | number;
}

export default function ChartTableView({
  chartId,
  title,
  sourceChartType,
  data,
  fillHeight,
  onSetChartType,
}: ChartTableViewProps) {
  const entry = getChartEntry(sourceChartType);
  const isDataTable = sourceChartType === "data_table";

  // Resolve columns and rows from the registry entry (or dynamically for data_table).
  const { columns, rows } = useMemo(() => {
    if (isDataTable) {
      const td = getGenericTableData(data);
      const dynColumns: TableColumnDef[] = td.headers.map((h) => ({
        key: h,
        header: h[0]?.toUpperCase() + h.slice(1),
      }));
      return { columns: dynColumns, rows: td.rows };
    }
    return {
      columns: entry?.tableColumns ?? [],
      rows: entry?.getTableRows?.(data) ?? [],
    };
  }, [data, entry, isDataTable]);

  // Determine which conversion options are available from table view.
  const canConvertBack = sourceChartType !== "data_table";
  const canBar =
    sourceChartType !== "line" &&
    sourceChartType !== "multi_line" &&
    sourceChartType !== "tiered_bar" &&
    sourceChartType !== "quadrant_scatter" &&
    canConvertToChart(sourceChartType, data);
  const canPie = canBar;

  const handleDownloadCsv = () => exportTableCsv(title, columns, rows);
  const handleDownloadXls = () => exportTableXls(title, columns, rows);

  // menuPos: coordinates of the header button that opened this menu.
  // Null = closed. Set via the tableMenuRegistry when WidgetRenderer's button is clicked.
  const [menuPos, setMenuPos] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    registerTableMenu(chartId, (pos) => setMenuPos(pos));
    return () => unregisterTableMenu(chartId);
  }, [chartId]);

  // Close on next outside click.
  // setTimeout defers the listener to the next task so the click that OPENED
  // the menu doesn't immediately bubble to document and close it again.
  useEffect(() => {
    if (!menuPos) return;
    const close = () => setMenuPos(null);
    const id = setTimeout(
      () => document.addEventListener("click", close, { once: true }),
      0,
    );
    return () => {
      clearTimeout(id);
      document.removeEventListener("click", close);
    };
  }, [menuPos]);

  const closeAndRun = (fn: () => void) => () => {
    fn();
    setMenuPos(null);
  };

  return (
    <div className={fillHeight ? "h-100 d-flex flex-column" : ""}>
      {/* Fixed-position menu anchored to the header button's coordinates */}
      {menuPos && (
        <ul
          className="dropdown-menu show shadow"
          style={{
            position: "fixed",
            top: 49,
            right: 47,
            // transform: "translateX(100%)",
            zIndex: 1050,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <li>
            <button
              className="dropdown-item"
              onClick={closeAndRun(handleDownloadCsv)}
            >
              Download CSV
            </button>
          </li>
          <li>
            <button
              className="dropdown-item"
              onClick={closeAndRun(handleDownloadXls)}
            >
              Download XLS
            </button>
          </li>
          {(canConvertBack || canBar || canPie) && (
            <li>
              <hr className="dropdown-divider" />
            </li>
          )}
          {canConvertBack && (
            <li>
              <button
                className="dropdown-item"
                onClick={closeAndRun(() => onSetChartType(sourceChartType))}
              >
                Convert to source chart
              </button>
            </li>
          )}
          {canBar && (
            <li>
              <button
                className="dropdown-item"
                onClick={closeAndRun(() => onSetChartType("bar"))}
              >
                Convert to bar chart
              </button>
            </li>
          )}
          {canPie && (
            <li>
              <button
                className="dropdown-item"
                onClick={closeAndRun(() => onSetChartType("pie"))}
              >
                Convert to pie chart
              </button>
            </li>
          )}
        </ul>
      )}

      <div
        className={`highcharts-factory-table-wrapper ${
          fillHeight ? "highcharts-factory-table-wrapper--fill" : ""
        }`}
      >
        <Table striped responsive className="mb-0 text-left">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={
                    col.align === "end"
                      ? "text-end"
                      : col.align === "center"
                        ? "text-center"
                        : ""
                  }
                >
                  {col.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIdx) => {
              const tierStyle =
                row.tier && typeof row.tier === "string"
                  ? (TIER_STYLES[row.tier] ?? DEFAULT_TIER_STYLE)
                  : null;

              return (
                <tr key={rowIdx}>
                  {columns.map((col) => {
                    // Tier column renders a styled badge instead of plain text.
                    if (col.cellType === "tier-badge" && tierStyle) {
                      return (
                        <td key={col.key} className="text-center">
                          <span
                            className="badge rounded-pill"
                            style={{
                              background: tierStyle.bg,
                              color: tierStyle.text,
                              border: `1px solid ${tierStyle.border}`,
                              fontSize: 11,
                              fontWeight: 500,
                            }}
                          >
                            <span
                              style={{ marginRight: 4, color: tierStyle.dot }}
                            >
                              ●
                            </span>
                            {String(row[col.key] ?? "")}
                          </span>
                        </td>
                      );
                    }

                    const val = formatCellValue(row[col.key], col);
                    // Bold the practice name column in quadrant scatter table.
                    const isBold =
                      col.key === columns[0]?.key &&
                      sourceChartType === "quadrant_scatter";

                    return (
                      <td
                        key={col.key}
                        className={[
                          col.align === "end"
                            ? "text-end"
                            : col.align === "center"
                              ? "text-center"
                              : "",
                          isBold ? "fw-medium" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {val}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
